import React from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import {
  Wrench,
  List,
  SlidersHorizontal,
  MessageSquare,
  Brain,
  BarChart3,
  FileText,
} from 'lucide-react';
import { useUserProfile } from '../../../../api/profile';
import { isPermitted } from '@plerionhq/design-system';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

interface SettingsNavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  children?: SettingsNavItem[];
  requiresPermission?: string[];
  /** sessionStorage key that must equal 'true' to show this item. */
  requiresFlag?: string;
}

const settingsNavItems = (
  t: TFunction<'pleri/settings'>,
): SettingsNavItem[] => [
  {
    id: 'preferences',
    label: t('preferences'),
    icon: SlidersHorizontal,
    to: '/settings/preferences',
  },
  {
    id: 'notification',
    label: t('notification'),
    icon: MessageSquare,
    to: '/settings/notification',
  },
  {
    id: 'memory',
    label: t('memory'),
    icon: Brain,
    to: '/settings/memory',
  },
  {
    id: 'tools',
    label: t('tools'),
    icon: Wrench,
    to: '/settings/tools',
  },
  {
    id: 'activities',
    label: t('activities'),
    icon: List,
    to: '/settings/activities',
  },
  {
    id: 'usage',
    label: t('usage'),
    icon: BarChart3,
    to: '/settings/usage',
    requiresPermission: ['pleri:OrgUsage', 'pleri:Write'],
  },
  {
    id: 'roi-reports',
    label: t('roiReports'),
    icon: FileText,
    to: '/settings/roi-reports',
    requiresPermission: ['pleri:ReadOnly', 'pleri:OrgUsage', 'pleri:Write'],
  },
];

interface SettingsLayoutProps {
  children: React.ReactNode;
}

/**
 * Layout component for settings pages with sidebar navigation
 */
export const SettingsLayout: React.FC<SettingsLayoutProps> = ({ children }) => {
  const { t } = useTranslation('pleri/settings');
  const location = useLocation();
  const { data: userProfile } = useUserProfile();

  // Normalize pathname (remove /pleri prefix if present, anchored to start)
  const pathname = location.pathname.replace(/^\/pleri/, '');

  // Filter navigation items based on permissions and feature flags
  const permissions = userProfile?.permissions || [];
  const filteredNavItems = settingsNavItems(t).filter((item) => {
    if (
      item.requiresPermission &&
      !isPermitted(permissions, item.requiresPermission, false)
    ) {
      return false;
    }
    if (item.requiresFlag) {
      try {
        return sessionStorage.getItem(item.requiresFlag) !== 'false';
      } catch {
        return false;
      }
    }
    return true;
  });

  // Check if a route is active
  const isActiveRoute = (routePath: string) => {
    if (pathname === routePath) {
      return true;
    }
    // Check if it's a child route
    if (pathname.startsWith(routePath + '/')) {
      return true;
    }
    return false;
  };

  return (
    <div className="flex h-full w-full flex-col bg-white dark:bg-gray-900">
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Navigation */}
        <aside className="w-48 border-r border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
          <nav className="p-4" aria-label={t('settingsNavigation')}>
            <ul className="space-y-1">
              {filteredNavItems.map((item) => {
                const isActive = isActiveRoute(item.to);
                const Icon = item.icon;

                return (
                  <li key={item.id}>
                    <Link
                      to={item.to}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'border border-orange-200 bg-orange-50 text-orange-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                      } `}
                      data-testid={`settings-nav-${item.id}`}
                    >
                      <Icon
                        className={`h-5 w-5 flex-shrink-0 ${
                          isActive
                            ? 'text-orange-600 dark:text-orange-500'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Settings Content - Full Width */}
        <main className="w-full flex-1 overflow-hidden bg-white dark:bg-gray-900">
          <div className="h-full w-full">{children}</div>
        </main>
      </div>
    </div>
  );
};
